import { useEffect, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface NavItemProps {
  children?: React.ReactNode;
  className?: string;
  depth: number;
  href?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  title: string;
  isOpenNav: boolean;
  selected?: boolean;
  open?: boolean;
  iconHref?: string;
  badge?: number;
  /** Show a colored dot badge instead of number */
  badgeDot?: boolean;
  /** Badge color: primary (blue), warning (yellow), success, error, info */
  badgeColor?: "primary" | "warning" | "success" | "error" | "info" | "secondary";
}

const NavItem: React.FC<NavItemProps> = ({
  children,
  depth,
  href,
  icon: Icon,
  title,
  isOpenNav,
  selected,
  open: isOpen,
  iconHref,
  badge,
  badgeDot,
  badgeColor = "primary",
}: NavItemProps) => {
  const [open, setOpen] = useState<boolean>(isOpen);

  const style = {
    paddingLeft: isOpenNav ? "8px" : "4px",
  };

  const address = href || (isOpenNav ? "" : iconHref);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <>
      <ListItemButton
        sx={style}
        component={RouterLink}
        to={address || "#"}
        selected={selected}
        onClick={(e) => {
          if (!address) {
            e.preventDefault();
          }
          e.stopPropagation();
          setOpen(!!address || !open);
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {badgeDot ? (
            <Badge
              variant="dot"
              color={badgeColor}
              sx={{
                "& .MuiBadge-badge": {
                  minWidth: 8,
                  height: 8,
                },
              }}
            >
              {Icon && <Icon />}
            </Badge>
          ) : badge && badge > 0 ? (
            <Badge
              badgeContent={badge}
              color={badgeColor}
              sx={{
                "& .MuiBadge-badge": {
                  minWidth: 16,
                  height: 16,
                  fontSize: "0.6rem",
                  padding: "0 4px",
                },
              }}
            >
              {Icon && <Icon />}
            </Badge>
          ) : (
            Icon && <Icon />
          )}
        </ListItemIcon>
        {isOpenNav && <ListItemText primary={title} />}
        {isOpenNav && children && (
          <ListItemIcon
            sx={{ minWidth: 30 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((o) => !o);
            }}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}{" "}
          </ListItemIcon>
        )}
      </ListItemButton>
      {children && (
        <Collapse in={open && isOpenNav}>
          <Box
            style={{
              paddingLeft: isOpenNav ? depth * 16 + 16 + "px" : 0,
            }}
          >
            {children}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default NavItem;
